import { Injectable } from '@angular/core'
import {
  HearingSectionResponse,
  HearingSectionUpdateRequest,
  ListHearingSectionsRequest,
} from '@ftr/annotations-contracts/v2'
import { ConflictApiError, NotFoundApiError, UnauthorizedApiError } from '@ftr/api-shared'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { ApiResult, RemoteData } from '@ftr/foundation'
import { AnnotationsApiClientFactory, AnnotationsApiClientFactoryV2 } from '../../api-client'
import { BadRequestApiError } from '../../api-client/errors'
import { AnnotationsService } from '../base/annotations.service'

@Injectable({
  providedIn: 'root',
})
export class HearingSectionsService extends AnnotationsService {
  constructor(
    apiClientFactory: AnnotationsApiClientFactory, // v1 for existing methods
    regionCache: CourtSystemRegionCache,
    apiClientFactoryV2: AnnotationsApiClientFactoryV2, // v2 for new methods
  ) {
    super(apiClientFactory, regionCache, apiClientFactoryV2)
  }

  listHearingSections({
    courtSystemId,
    caseId,
    offset,
    limit,
    sortDirection,
  }: ListHearingSectionsRequest & {
    courtSystemId: Uuid
  }): ApiResult<{
    items: HearingSectionResponse[]
    total: number
  }> {
    return this.performApiRequestV2({
      courtSystemId,
      requestFn: client =>
        client.hearingSections.listHearingSections({
          params: { courtSystemId },
          query: { caseId, offset, limit, sortDirection },
        }),
      responseMapFn: result => {
        if (result.status === 404) {
          return RemoteData.failure(new NotFoundApiError())
        }
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }
        if (result.status === 400) {
          return RemoteData.failure(new BadRequestApiError(result.body.error))
        }

        if (result.status === 200) {
          return RemoteData.success(result.body)
        }

        return RemoteData.failure(new Error('Unknown error'))
      },
    })
  }

  getHearingSection({
    courtSystemId,
    hearingSectionId,
  }: {
    courtSystemId: Uuid
    hearingSectionId: Uuid
  }): ApiResult<HearingSectionResponse> {
    return this.performApiRequestV2({
      courtSystemId,
      requestFn: client => client.hearingSections.getHearingSection({ params: { courtSystemId, hearingSectionId } }),
      responseMapFn: result => {
        if (result.status === 404) {
          return RemoteData.failure(new NotFoundApiError())
        }
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }
        if (result.status === 400) {
          return RemoteData.failure(new BadRequestApiError(result.body.error))
        }

        if (result.status === 200) {
          return RemoteData.success(result.body)
        }

        return RemoteData.failure(new Error('Unknown error'))
      },
    })
  }

  updateHearingSection({
    courtSystemId,
    hearingSectionId,
    updatedHearingSection,
  }: {
    courtSystemId: Uuid
    hearingSectionId: Uuid
    updatedHearingSection: HearingSectionUpdateRequest
  }): ApiResult<HearingSectionResponse> {
    return this.performApiRequestV2({
      courtSystemId,
      requestFn: client =>
        client.hearingSections.updateHearingSection({
          params: { courtSystemId, hearingSectionId },
          body: updatedHearingSection,
        }),
      responseMapFn: result => {
        if (result.status === 409) {
          return RemoteData.failure(new ConflictApiError(result.body.error))
        }
        if (result.status === 404) {
          return RemoteData.failure(new NotFoundApiError())
        }
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }
        if (result.status === 400) {
          return RemoteData.failure(new BadRequestApiError(result.body.error))
        }

        if (result.status === 200) {
          return RemoteData.success(result.body)
        }

        return RemoteData.failure(new Error('Unknown error'))
      },
    })
  }

  deleteHearingSection({
    courtSystemId,
    hearingSectionId,
  }: {
    courtSystemId: Uuid
    hearingSectionId: Uuid
  }): ApiResult<void> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client => client.hearingSection.deleteHearingSection({ query: { hearingSectionId } }),
      responseMapFn: result => {
        if (result.status === 404) {
          return RemoteData.failure(new NotFoundApiError())
        }
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }

        if (result.status === 204) {
          return RemoteData.success(undefined)
        }

        return RemoteData.failure(new Error('Unknown error'))
      },
    })
  }
}
